/**
 * Permission constants — type-safe references to the FreighAI permission catalog.
 *
 * Per RBAC Module spec #1. The backend measures the counts rather than maintaining them by
 * hand — `Permissions.USER_ASSIGNABLE.size` / `.ALL.size` — because every hand-written count
 * in that file and this one had drifted, in three different directions, before anyone
 * noticed. This file previously claimed "210 user-assignable + 8 agent = 218 total", which
 * was wrong when written and stayed wrong. Do not restore a number here; read it off the
 * backend if you need one.
 *
 * Mirrors `shared/freighai-common/src/main/kotlin/com/freighai/common/dto/Permissions.kt`
 * on the backend. When the backend catalog changes, update both files in lockstep.
 *
 * Use these constants instead of hardcoded strings:
 *   const canEdit = useHasPermission(Permissions.CUSTOMER_EDIT)
 */

export const Permissions = {
  // 1. Customer & contact (20)
  CUSTOMER_VIEW: 'customer:view',
  CUSTOMER_CREATE: 'customer:create',
  CUSTOMER_EDIT: 'customer:edit',
  CUSTOMER_DELETE: 'customer:delete',
  CUSTOMER_REASSIGN_OWNER: 'customer:reassign-owner',
  CUSTOMER_MERGE: 'customer:merge',
  CUSTOMER_DEACTIVATE: 'customer:deactivate',
  CUSTOMER_EXPORT: 'customer:export',
  ADDRESS_VIEW: 'address:view',
  ADDRESS_EDIT: 'address:edit',
  // The customer's address book (saved shippers / consignees) — separate from the customer's
  // own addresses above. BINARY: the book is not record-scoped.
  ADDRESS_BOOK_VIEW: 'address-book:view',     // see the book, search it, pick an entry
  ADDRESS_BOOK_EDIT: 'address-book:edit',     // save, add, edit entries
  ADDRESS_BOOK_DELETE: 'address-book:delete', // delete entries (split from edit, 2026-10-26)
  CUSTOMER_NOTE_VIEW: 'customer-note:view',
  CUSTOMER_NOTE_CREATE: 'customer-note:create',
  CUSTOMER_CONTACT_VIEW: 'customer-contact:view',
  CUSTOMER_CONTACT_CREATE: 'customer-contact:create',
  CUSTOMER_CONTACT_EDIT: 'customer-contact:edit',
  CUSTOMER_CONTACT_DELETE: 'customer-contact:delete',
  CUSTOMER_DOCUMENT_UPLOAD: 'customer-document:upload',
  CUSTOMER_DOCUMENT_DOWNLOAD: 'customer-document:download',
  CUSTOMER_DOCUMENT_DELETE: 'customer-document:delete',
  /**
   * Seeing the customer LIST — the Customers / Companies menu entries and pages. A screen switch
   * only: customer reads stay on CUSTOMER_VIEW, so without it a user still searches customers on
   * a quote, reads the customer on a task or job, and opens one customer from a link
   * (see ./customerListAccess).
   */
  CUSTOMER_LIST_VIEW: 'customer-list:view',

  // 2. Quotations (17)
  QUOTE_VIEW: 'quote:view',
  QUOTE_CREATE: 'quote:create',
  QUOTE_EDIT: 'quote:edit',
  QUOTE_EDIT_VERSION: 'quote:edit-version',
  QUOTE_EDIT_EMAIL_CONTEXT: 'quote:edit-email-context',
  QUOTE_APPROVE: 'quote:approve',
  QUOTE_REJECT: 'quote:reject',
  QUOTE_SEND: 'quote:send',
  QUOTE_CANCEL: 'quote:cancel',
  QUOTE_EXPORT: 'quote:export',
  QUOTE_LINK_JOB_ORDER: 'quote:link-job-order',
  QUOTE_FETCH_RATES: 'quote:fetch-rates',
  QUOTE_ADD_PROVIDER_RESPONSE: 'quote:add-provider-response',
  QUOTE_PROVIDER_SELECTION: 'quote:provider-selection',
  QUOTE_SKIP_FOLLOW_UPS: 'quote:skip-follow-ups',
  QUOTE_TAG_VIEW: 'quote-tag:view',
  QUOTE_TAG_EDIT: 'quote-tag:edit',

  // 3. Job Orders (19)
  ORDER_VIEW: 'order:view',
  ORDER_CREATE: 'order:create',
  ORDER_EDIT: 'order:edit',
  ORDER_CANCEL: 'order:cancel',
  ORDER_DELETE: 'order:delete',
  ORDER_REASSIGN_OWNER: 'order:reassign-owner',
  ORDER_GENERATE_AWB: 'order:generate-awb',
  ORDER_AWB_CLEAR: 'order:awb:clear',
  ORDER_UPDATE_TRACKING: 'order:update-tracking',
  ORDER_UPDATE_ACTUALS: 'order:update-actuals',
  ORDER_MARK_DELIVERED: 'order:mark-delivered',
  ORDER_EXPORT: 'order:export',
  ORDER_UPLOAD_DOCUMENT: 'order:upload-document',
  ORDER_RECONCILIATION: 'order:reconciliation',
  ORDER_EDIT_INTERNALS: 'order:edit-internals',
  ACTUAL_COST_VIEW: 'actual-cost:view',
  ACTUAL_COST_CREATE: 'actual-cost:create',
  ORDER_TAG_VIEW: 'order-tag:view',
  ORDER_TAG_EDIT: 'order-tag:edit',

  // 3b. Freight Booking Module (Freight-Booking-Build-Spec §15.6).
  // Deliberately NOT order:edit — a booking desk places and confirms bookings
  // without the right to edit the commercial job, and recording that a leg
  // physically moved is narrower than either.
  JO_BOOKING_READ: 'jo-booking:read',
  JO_BOOKING_WRITE: 'jo-booking:write',
  JO_LEGS_STATUS: 'jo-legs:status',
  // Issuing a house document mints a negotiable instrument, and authorising a release
  // gives cargo away — §15.6 keeps both apart from jo-booking:write for that reason.
  JO_DOCUMENTS_ISSUE: 'jo-documents:issue',
  JO_DOCUMENTS_RELEASE: 'jo-documents:release',

  // 4. Tasks — entity-level (7)
  TASK_VIEW: 'task:view',
  TASK_CLAIM: 'task:claim',
  TASK_REASSIGN: 'task:reassign',
  TASK_ESCALATE: 'task:escalate',
  TASK_CANCEL: 'task:cancel',
  TASK_CREATE: 'task:create',
  TASK_EXPORT: 'task:export',

  // 4b. Task-type (9)
  TASK_APPROVAL_QUOTATION: 'task:approval:quotation',
  TASK_APPROVAL_JOB_ORDER: 'task:approval:job-order',
  TASK_DECISION_QUOTATION: 'task:decision:quotation',
  TASK_DECISION_JOB_ORDER: 'task:decision:job-order',
  TASK_ACTION_QUOTATION: 'task:action:quotation',
  TASK_ACTION_JOB_ORDER: 'task:action:job-order',
  TASK_EXCEPTION_QUOTATION: 'task:exception:quotation',
  TASK_EXCEPTION_JOB_ORDER: 'task:exception:job-order',
  TASK_MANUAL_ANY: 'task:manual:any',

  // 5. Finance — transactions (22)
  VOUCHER_VIEW: 'voucher:view',
  VOUCHER_CREATE: 'voucher:create',
  VOUCHER_EDIT: 'voucher:edit',
  VOUCHER_POST: 'voucher:post',
  VOUCHER_CANCEL: 'voucher:cancel',
  VOUCHER_REVERSE: 'voucher:reverse',
  VOUCHER_VOID: 'voucher:void',
  VOUCHER_EXPORT: 'voucher:export',
  QUICK_ENTRY_CREATE: 'quick-entry:create',
  INVOICE_VIEW: 'invoice:view',
  INVOICE_CREATE: 'invoice:create',
  INVOICE_EDIT: 'invoice:edit',
  INVOICE_POST: 'invoice:post',
  INVOICE_VOID: 'invoice:void',
  INVOICE_EXPORT: 'invoice:export',
  ALLOCATION_VIEW: 'allocation:view',
  ALLOCATION_CREATE: 'allocation:create',
  ALLOCATION_REVERSE: 'allocation:reverse',
  ALLOCATION_DELETE: 'allocation:delete',
  CHEQUE_VIEW: 'cheque:view',
  CHEQUE_CREATE: 'cheque:create',
  CHEQUE_CLEAR: 'cheque:clear',
  CHEQUE_BOUNCE: 'cheque:bounce',

  // 6. Finance — masters (24)
  LEDGER_VIEW: 'ledger:view',
  LEDGER_CREATE: 'ledger:create',
  LEDGER_EDIT: 'ledger:edit',
  LEDGER_DELETE: 'ledger:delete',
  LEDGER_GROUP_VIEW: 'ledger-group:view',
  LEDGER_GROUP_EDIT: 'ledger-group:edit',
  SUBLEDGER_VIEW: 'subledger:view',
  SUBLEDGER_EDIT: 'subledger:edit',
  SUBLEDGER_TYPE_VIEW: 'subledger-type:view',
  SUBLEDGER_TYPE_EDIT: 'subledger-type:edit',
  VOUCHER_TYPE_VIEW: 'voucher-type:view',
  VOUCHER_TYPE_EDIT: 'voucher-type:edit',
  PAYMENT_METHOD_VIEW: 'payment-method:view',
  PAYMENT_METHOD_EDIT: 'payment-method:edit',
  ACCOUNT_MAPPING_VIEW: 'account-mapping:view',
  ACCOUNT_MAPPING_EDIT: 'account-mapping:edit',
  FISCAL_PERIOD_VIEW: 'fiscal-period:view',
  FISCAL_PERIOD_EDIT: 'fiscal-period:edit',
  FISCAL_PERIOD_CLOSE: 'fiscal-period:close',
  FISCAL_PERIOD_LOCK: 'fiscal-period:lock',
  FISCAL_PERIOD_REOPEN: 'fiscal-period:reopen',
  CURRENCY_VIEW: 'currency:view',
  CURRENCY_EDIT: 'currency:edit',
  AUDIT_TRAIL_VIEW: 'audit-trail:view',

  // 7. Master Data (24)
  SERVICE_PROVIDER_VIEW: 'service-provider:view',
  SERVICE_PROVIDER_CREATE: 'service-provider:create',
  SERVICE_PROVIDER_EDIT: 'service-provider:edit',
  SERVICE_PROVIDER_DELETE: 'service-provider:delete',
  SERVICE_PROVIDER_MERGE: 'service-provider:merge',
  SERVICE_PROVIDER_BLACKLIST: 'service-provider:blacklist',
  CARRIER_VIEW: 'carrier:view',
  CARRIER_CREATE: 'carrier:create',
  CARRIER_EDIT: 'carrier:edit',
  CARRIER_DELETE: 'carrier:delete',
  CARRIER_INTEGRATION_VIEW: 'carrier-integration:view',
  CARRIER_INTEGRATION_EDIT: 'carrier-integration:edit',
  RATE_CARD_VIEW: 'rate-card:view',
  RATE_CARD_CREATE: 'rate-card:create',
  RATE_CARD_EDIT: 'rate-card:edit',
  RATE_CARD_DELETE: 'rate-card:delete',
  SURCHARGE_VIEW: 'surcharge:view',
  SURCHARGE_EDIT: 'surcharge:edit',
  CHARGE_TYPE_VIEW: 'charge-type:view',
  CHARGE_TYPE_EDIT: 'charge-type:edit',
  ALERT_VIEW: 'alert:view',
  ALERT_DISMISS: 'alert:dismiss',
  MASTER_LAYOUT_VIEW: 'master-layout:view',
  MASTER_LAYOUT_EDIT: 'master-layout:edit',
  DOCUMENT_TEMPLATE_VIEW: 'document-template:view',
  DOCUMENT_TEMPLATE_EDIT: 'document-template:edit',

  // 8. User Management & RBAC (21)
  USER_VIEW: 'user:view',
  USER_INVITE: 'user:invite',
  USER_EDIT: 'user:edit',
  USER_EDIT_ROLE: 'user:edit-role',
  USER_EDIT_PERMISSIONS: 'user:edit-permissions',
  USER_EDIT_TEAMS: 'user:edit-teams',
  USER_DEACTIVATE: 'user:deactivate',
  USER_RESET_PASSWORD: 'user:reset-password',
  USER_EXPORT: 'user:export',
  ROLE_VIEW: 'role:view',
  ROLE_CREATE: 'role:create',
  ROLE_EDIT: 'role:edit',
  ROLE_DELETE: 'role:delete',
  TEAM_VIEW: 'team:view',
  TEAM_CREATE: 'team:create',
  TEAM_EDIT: 'team:edit',
  TEAM_DELETE: 'team:delete',
  TEAM_ADD_MEMBER: 'team:add-member',
  TEAM_REMOVE_MEMBER: 'team:remove-member',
  RBAC_OVERRIDE_POLICY: 'rbac:override-policy',
  AUDIT_LOG_VIEW: 'audit-log:view',

  // 9. Tenant Settings (21)
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  AUTONOMY_VIEW: 'autonomy:view',
  AUTONOMY_TOGGLE: 'autonomy:toggle',
  AUTONOMY_SEED: 'autonomy:seed',
  EMAIL_SETTINGS_VIEW: 'email-settings:view',
  EMAIL_SETTINGS_EDIT: 'email-settings:edit',
  FINANCE_SETTINGS_VIEW: 'finance-settings:view',
  FINANCE_SETTINGS_EDIT: 'finance-settings:edit',
  QUOTATION_SETTINGS_VIEW: 'quotation-settings:view',
  QUOTATION_SETTINGS_EDIT: 'quotation-settings:edit',
  JOB_ORDER_SETTINGS_VIEW: 'job-order-settings:view',
  JOB_ORDER_SETTINGS_EDIT: 'job-order-settings:edit',
  TASK_SETTINGS_VIEW: 'task-settings:view',
  TASK_SETTINGS_EDIT: 'task-settings:edit',
  REPORT_SETTINGS_VIEW: 'report-settings:view',
  REPORT_SETTINGS_EDIT: 'report-settings:edit',
  BANK_ACCOUNT_VIEW: 'bank-account:view',
  BANK_ACCOUNT_EDIT: 'bank-account:edit',
  SOA_AUTO_SEND_VIEW: 'soa-auto-send:view',
  SOA_AUTO_SEND_EDIT: 'soa-auto-send:edit',

  // 10. AI / Email / Workflow (13)
  EMAIL_ACCOUNT_VIEW: 'email-account:view',
  EMAIL_ACCOUNT_CONNECT: 'email-account:connect',
  EMAIL_ACCOUNT_DISCONNECT: 'email-account:disconnect',

  // 10b. WhatsApp channel (CONTRACTS §9 — verbatim; there are NO :connect/:disconnect strings —
  // connect, disconnect, rename and purposes-edit all gate on `whatsapp-account:manage`).
  WHATSAPP_ACCOUNT_VIEW: 'whatsapp-account:view',
  WHATSAPP_ACCOUNT_MANAGE: 'whatsapp-account:manage',
  WHATSAPP_CONVERSATION_VIEW: 'whatsapp-conversation:view',
  WHATSAPP_CONVERSATION_MANAGE: 'whatsapp-conversation:manage',
  // WhatsApp-engine tenant settings (G2 — persona, quiet hours, disclosure policy).
  WHATSAPP_SETTINGS_VIEW: 'whatsapp-settings:view',
  WHATSAPP_SETTINGS_EDIT: 'whatsapp-settings:edit',
  EMAIL_THREAD_VIEW: 'email-thread:view',
  EMAIL_THREAD_REPLY: 'email-thread:reply',
  EMAIL_DRAFT_VIEW: 'email-draft:view',
  EMAIL_DRAFT_CREATE: 'email-draft:create',
  EMAIL_DRAFT_EDIT: 'email-draft:edit',
  EMAIL_DRAFT_DELETE: 'email-draft:delete',
  WORKFLOW_VIEW: 'workflow:view',
  WORKFLOW_CANCEL: 'workflow:cancel',
  WORKFLOW_REPLAY: 'workflow:replay',
  AGENT_EXECUTE: 'agent:execute',

  // 11. Reports (14)
  REPORT_STATEMENT_CUSTOMER: 'report:statement-customer',
  REPORT_STATEMENT_VENDOR: 'report:statement-vendor',
  REPORT_AGING_RECEIVABLES: 'report:aging-receivables',
  REPORT_AGING_PAYABLES: 'report:aging-payables',
  REPORT_QUOTATION: 'report:quotation',
  REPORT_BOOKING_TURNAROUND: 'report:booking-turnaround',
  REPORT_TRIAL_BALANCE: 'report:trial-balance',
  REPORT_BALANCE_SHEET: 'report:balance-sheet',
  REPORT_PROFIT_LOSS: 'report:profit-loss',
  REPORT_GENERAL_LEDGER: 'report:general-ledger',
  REPORT_CASH_POSITION: 'report:cash-position',
  REPORT_VAT: 'report:vat',
  // Book of business. The /sales/book-of-business route and its nav entry require BOTH the
  // company-head email check AND this permission (the gateway row for its one endpoint); neither
  // alone shows the page. New tenants' ADMIN is seeded with it; existing tenants get it on ADMIN only
  // from scripts/rbac/21_grant_report_perms.py (DECISIONS.md D25), which must run BEFORE the frontend
  // deploy: until it has, the company head sees the access-required state instead of the page.
  REPORT_ACCOUNTS_REVENUE: 'report:accounts-revenue',
  // Provisional Report (DECISIONS.md D3). Gates the /reports/provisional route, its nav entry and
  // both job-order-service endpoints; MUST equal the gateway row in endpoint-permissions.yaml
  // exactly, or the page loads and then 403s on every call. No role holds it yet, so the report
  // ships inert until it is granted per tenant.
  REPORT_PROVISIONAL: 'report:provisional',

  // 12. Module access (3)
  OPERATIONS_ACCESS: 'operations:access',
  FINANCE_ACCESS: 'finance:access',
  SETTINGS_ACCESS: 'settings:access',

  // 13. Sales (v1 · F1-F11 · added 2026-05-27 for Lead Management module)
  SALES_ACCESS: 'sales:access',
  LEAD_VIEW: 'lead:view',
  LEAD_CREATE: 'lead:create',
  LEAD_EDIT: 'lead:edit',
  LEAD_DELETE: 'lead:delete',
  LEAD_ASSIGN: 'lead:assign',
  LEAD_CHANGE_STAGE: 'lead:change_stage',
  SALES_SETTINGS_EDIT: 'sales-settings:edit',

  // 14. Activity (4) — cross-module capability hosted in master-data.
  // Mirrors shared Permissions.kt. activity:view is record-scopable (which activities you
  // see, by assignee); activity:assign gates assigning to another user (TEAM/ALL).
  //
  // These replaced `followup:*`, which the backend resolved alongside them for the whole of
  // release 1 so the enforcement flip stayed reversible by redeploy. Phase 4 removed it from
  // the backend catalog; nothing here ever mirrored it.
  ACTIVITY_VIEW: 'activity:view',
  ACTIVITY_ASSIGN: 'activity:assign',
  // Decision 16, and the reason the write gates exist at all: under the old model POST
  // /follow-ups was gated on a single view permission, so VIEWER — which deliberately holds
  // no write permission anywhere — could create work tenant-wide. The gateway now requires these two
  // (activity:create on POST, activity:edit on both PATCHes), so any button that writes must
  // be gated on them or it renders for people the gateway will 403.
  ACTIVITY_CREATE: 'activity:create',
  ACTIVITY_EDIT: 'activity:edit',

  // 15. Action Items (8) — internal task management hosted in master-data.
  // Mirrors shared Permissions.kt section 13c. Distinct from the agent checkpoint
  // queue's task:* permissions. action-item:view is record-scopable (assignee/watcher);
  // action-item:assign is scope-configurable (self/team/all).
  ACTION_ITEM_VIEW: 'action-item:view',
  ACTION_ITEM_CREATE: 'action-item:create',
  ACTION_ITEM_EDIT: 'action-item:edit',
  ACTION_ITEM_ASSIGN: 'action-item:assign',
  ACTION_ITEM_ESCALATE: 'action-item:escalate',
  ACTION_ITEM_DELETE: 'action-item:delete',
  ACTION_ITEM_SETTINGS_VIEW: 'action-item-settings:view',
  ACTION_ITEM_SETTINGS_EDIT: 'action-item-settings:edit',
  NOTIFICATION_SETTINGS_VIEW: 'notification-settings:view',
  NOTIFICATION_SETTINGS_EDIT: 'notification-settings:edit',
  // 16. Onboarding (8 — customer onboarding cases, hosted in sales-service).
  // Mirrors shared Permissions.kt section 13e. The case is the record a rep fills to take a
  // customer from UNRECOGNIZED to activated.
  //
  // ONBOARDING_CASE_APPROVE makes a user ELIGIBLE to be named in a document's approval chain;
  // being placed in one by the rep is what makes them responsible. There is no `complete` —
  // completion is the consequence of the last signature, so it is gated by RECORD_SIGNATURE.
  ONBOARDING_ACCESS: 'onboarding:access',
  ONBOARDING_CASE_VIEW: 'onboarding-case:view',   // SCOPED — by salesRepUserId
  ONBOARDING_CASE_CREATE: 'onboarding-case:create',
  ONBOARDING_CASE_EDIT: 'onboarding-case:edit',
  ONBOARDING_CASE_REQUEST: 'onboarding-case:request',
  ONBOARDING_CASE_APPROVE: 'onboarding-case:approve',
  ONBOARDING_CASE_RECORD_SIGNATURE: 'onboarding-case:record-signature',
  ONBOARDING_CASE_LINK_REVOKE: 'onboarding-case:link-revoke',
  // Cancel or archive an UNFINISHED case (Kiko #44) — the Sales Head: ADMIN and MANAGER only.
  ONBOARDING_CASE_CANCEL: 'onboarding-case:cancel',
  // The tenant's onboarding CONFIGURATION, not the cases. Read is split from write on purpose:
  // an approver is a step in the approval order and benefits from seeing where they sit in it,
  // while re-ordering it is an administrative act. VIEW is what puts the settings affordance in
  // the onboarding registry header — the operators' actual ask was to reach settings from the
  // case list, and gating the read behind the write would hide it from everyone who asked.
  ONBOARDING_SETTINGS_VIEW: 'onboarding-settings:view',
  ONBOARDING_SETTINGS_EDIT: 'onboarding-settings:edit',

  // Schedule / calendar (schedule module, phase 7 backend · phase 10 here). CALENDAR_EVENT_VIEW
  // is record-SCOPED server-side — which blocks you see is decided there by owner/participant,
  // so holding it never means "the whole tenant's calendar". The other two are binary.
  CALENDAR_EVENT_VIEW: 'calendar-event:view',
  CALENDAR_EVENT_CREATE: 'calendar-event:create',
  CALENDAR_EVENT_EDIT: 'calendar-event:edit',
} as const

/**
 * String-literal type union for all permission strings. Lets callers narrow:
 *   function foo(perm: Permission) { ... }
 */
export type Permission = typeof Permissions[keyof typeof Permissions]

/**
 * Canonical `ApiResponse<T>` envelope shape — hoisted from feature folders
 * so every consumer references the same definition. Per spec #10 §6.4.
 */
export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  timestamp?: string
  errorCode?: string
  details?: unknown
}

/**
 * Standard 403 details body shape per RBAC Module spec #7 §6.5.
 */
export interface AuthzErrorDetails {
  required_permission?: string | null
  scope_required?: string | null
  actor_scope?: string | null
  resource_type?: string | null
}
